import { Link } from 'react-router-dom';
import styles from './CostOverview.module.css';

export interface OverviewTotals {
  totalUsd: number; computeUsd: number; storageUsd: number; transferUsd: number; fixedUsd: number;
  cdnCreditUsd: number; jobs: number; measuredJobs: number; runHours: number;
  storageGb: number; storageCoverage: number; downloads: number; downloadGb: number;
  viewUsd: number; views: number; reportedViews: number;
}
export interface StorageMeasurement {
  coverage: number; recordedGb: number; estimatedGb: number; folders: number;
  recordedFiles: number; estimatedFiles: number; lastRecordedAt: string | null;
  endAt: string; endGb: number; endRecordedGb: number; endEstimatedGb: number;
  byKind: { key: string; label: string; gb: number }[];
}

type LineKey = 'fixed' | 'storage' | 'transfer' | 'compute';
type Status = 'measured' | 'partial' | 'estimated' | 'entered' | 'none';

interface Line {
  key: LineKey; label: string; usd: number; sub: string; group: 'always' | 'use';
  measuredShare: number; status: Status; why: string;
}

const STATUS_LABEL: Record<Status, string> = {
  measured: 'Measured', partial: 'Partly measured', estimated: 'Estimated', entered: 'Entered', none: 'No cost',
};

const usd = (n: number) => `$${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n: number, dp = 1) => (n ?? 0).toLocaleString('en-US', { maximumFractionDigits: dp });
const pct = (share: number) => (share > 0 && share < 0.005 ? '<1%' : `${Math.round((share ?? 0) * 100)}%`);
const cents = (share: number) => {
  const c = share * 100;
  if (c <= 0) return '0¢';
  return c < 1 ? `${c.toFixed(1)}¢` : `${Math.round(c)}¢`;
};
const statusOf = (share: number): Status => (share >= 0.995 ? 'measured' : share > 0 ? 'partial' : 'estimated');

const niceMax = (v: number) => {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 5, 10]) if (v <= m * p) return m * p;
  return 10 * p;
};

// On a flat CloudFront plan the viewer cost is the plan's price, whatever the
// traffic; only pay-as-you-go prices the sessions that never reported.
const transferEstimatedUsd = (t: OverviewTotals, cdnPlan: string) => (
  cdnPlan === 'payg' && t.views > 0 ? t.viewUsd * ((t.views - t.reportedViews) / t.views) : 0
);

export const costLines = (t: OverviewTotals, cdnPlan: string): Line[] => {
  const computeShare = t.jobs > 0 ? t.measuredJobs / t.jobs : 1;
  const transferEst = transferEstimatedUsd(t, cdnPlan);
  const transferShare = t.transferUsd > 0 ? 1 - transferEst / t.transferUsd : 1;
  const unreported = t.views - t.reportedViews;
  return [
    {
      key: 'fixed', label: 'Fixed / always-on', usd: t.fixedUsd, sub: 'app servers, EBS', group: 'always',
      measuredShare: 1, status: t.fixedUsd > 0 ? 'entered' : 'none',
      why: 'Typed into Unit prices: it does not change with use.',
    },
    {
      key: 'storage', label: 'Storage', usd: t.storageUsd, sub: `${num(t.storageGb)} GB in S3`, group: 'use',
      measuredShare: t.storageCoverage, status: t.storageUsd > 0 ? statusOf(t.storageCoverage) : 'none',
      why: `${pct(t.storageCoverage)} of the stored GB-days come from folder records; files without a record are estimated as upload × their type's factor.`,
    },
    {
      key: 'transfer', label: 'Data transfer', usd: t.transferUsd,
      sub: `${num(t.downloads, 0)} downloads, ${num(t.downloadGb)} GB`, group: 'use',
      measuredShare: transferShare, status: t.transferUsd > 0 ? statusOf(transferShare) : 'none',
      why: transferEst > 0
        ? `Downloads are measured. ${num(unreported, 0)} of ${num(t.views, 0)} viewer sessions sent no report and are estimated.`
        : 'Downloads are measured; viewing costs the CloudFront plan\'s price or what the viewers reported.',
    },
    {
      key: 'compute', label: 'Conversion compute', usd: t.computeUsd,
      sub: `${num(t.jobs, 0)} jobs, ${num(t.runHours)} h`, group: 'use',
      measuredShare: computeShare, status: t.computeUsd > 0 ? statusOf(computeShare) : 'none',
      why: `${num(t.measuredJobs, 0)} of ${num(t.jobs, 0)} jobs have measured machine time; the rest are priced at run time × the lane rate.`,
    },
  ];
};

const Swatch = ({ line, estimated = false }: { line: LineKey | 'neutral'; estimated?: boolean }) => (
  <i className={`${styles.swatch} ${styles[line]} ${estimated ? styles.striped : ''}`} aria-hidden="true" />
);

const CostOverview = ({ totals, cdnPlan, rangeDays }: { totals: OverviewTotals; cdnPlan: string; rangeDays: number }) => {
  const lines = costLines(totals, cdnPlan);
  const gross = lines.reduce((a, l) => a + Math.max(0, l.usd), 0);
  const byShare = [...lines].sort((a, b) => b.usd - a.usd);
  const max = niceMax(Math.max(...lines.map((l) => l.usd), 0));
  const ticks = [0, 0.5, 1].map((f) => max * f);

  return (
    <div className={styles.overview}>
      <div className={styles.panel}>
        <h3 className={styles.title}>Of every dollar spent</h3>
        {gross > 0 ? (
          <>
            <div
              className={styles.whole}
              role="img"
              aria-label={byShare.map((l) => `${l.label} ${cents(l.usd / gross)}`).join(', ')}
            >
              {byShare.filter((l) => l.usd > 0).map((l) => (
                <div
                  key={l.key}
                  className={`${styles.seg} ${styles[l.key]}`}
                  style={{ flexGrow: l.usd }}
                  data-tip={`${l.label}: ${usd(l.usd)} of ${usd(gross)} (${pct(l.usd / gross)})`}
                  tabIndex={0}
                />
              ))}
            </div>
            <div className={styles.keys}>
              {byShare.map((l) => (
                <span key={l.key} className={styles.key}>
                  <Swatch line={l.key} />
                  {l.label} <b>{cents(l.usd / gross)}</b>
                </span>
              ))}
            </div>
          </>
        ) : (
          <p className={styles.muted}>Nothing was spent in this range.</p>
        )}
      </div>

      <div className={styles.panel}>
        <h3 className={styles.title}>Cost per line, last {rangeDays} days</h3>
        <div className={styles.chart}>
          {(['always', 'use'] as const).map((group) => (
            <div key={group} className={styles.group}>
              <p className={styles.groupName}>{group === 'always' ? 'Always on' : 'Grows with use'}</p>
              {lines.filter((l) => l.group === group).map((l) => (
                <div key={l.key} className={styles.row} data-tip={`${l.label}: ${usd(l.usd)} · ${l.sub}`} tabIndex={0}>
                  <div className={styles.label}>
                    <span className={styles.name}>{l.label} <span className={styles.value}>{usd(l.usd)}</span></span>
                    <span className={styles.sub}>{l.sub}</span>
                  </div>
                  <div className={styles.track}>
                    <div className={`${styles.bar} ${styles[l.key]}`} style={{ width: `${Math.min(100, (l.usd / max) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ))}
          <div className={styles.axis} aria-hidden="true">
            <span />
            <div className={styles.ticks}>
              {ticks.map((v, i) => <span key={i} style={{ left: `${(i / (ticks.length - 1)) * 100}%` }}>{usd(v).replace(/\.00$/, '')}</span>)}
            </div>
          </div>
        </div>
        <p className={styles.small}>
          {lines.map((l) => usd(l.usd)).join(' + ')}
          {totals.cdnCreditUsd > 0 && ` − ${usd(totals.cdnCreditUsd)} CloudFront plan credit`}
          {' '}= {usd(totals.totalUsd)}
        </p>
      </div>

      <div className={styles.panel}>
        <h3 className={styles.title}>How sure each line is</h3>
        <div className={styles.keys}>
          <span className={styles.key}><Swatch line="neutral" />Measured</span>
          <span className={styles.key}><Swatch line="neutral" estimated />Estimated</span>
        </div>
        <div className={styles.chart}>
          {lines.map((l) => (
            <div key={l.key} className={styles.row} data-tip={l.why} tabIndex={0}>
              <div className={styles.label}>
                <span className={styles.name}>
                  {l.label}
                  <span className={`${styles.chip} ${styles[`chip_${l.status}`]}`}>{STATUS_LABEL[l.status]}</span>
                </span>
                <span className={styles.sub}>
                  {l.status === 'entered' ? 'from Unit prices' : l.status === 'none' ? '—' : `${pct(l.measuredShare)} measured`}
                </span>
              </div>
              <div className={styles.track}>
                {l.status !== 'none' && (
                  <div className={styles.stack}>
                    {l.measuredShare > 0 && <div className={`${styles.stackSeg} ${styles[l.key]}`} style={{ flexGrow: l.measuredShare }} />}
                    {l.measuredShare < 1 && (
                      <div className={`${styles.stackSeg} ${styles[l.key]} ${styles.striped}`} style={{ flexGrow: 1 - l.measuredShare }} />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const HOUR_MS = 3600 * 1000;

export const StorageOverview = ({ s, storageUsd }: { s: StorageMeasurement; storageUsd: number }) => {
  const total = s.recordedGb + s.estimatedGb;
  const end = new Date(s.endAt).getTime();
  const endsNow = Date.now() - end < HOUR_MS;
  // A range that ends earlier ends at midnight (UTC) after its last day.
  const endLabel = endsNow ? 'Held now' : `Held at the end of ${new Date(end - 1).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  })}`;
  const max = niceMax(Math.max(total, ...s.byKind.map((k) => k.gb), 0));
  const rows = [
    {
      key: 'recorded', label: 'From folder records', gb: s.recordedGb, estimated: false,
      sub: `${num(s.folders, 0)} folders of ${num(s.recordedFiles, 0)} files`,
    },
    {
      key: 'estimated', label: 'Estimated', gb: s.estimatedGb, estimated: true,
      sub: `${num(s.estimatedFiles, 0)} files with no record yet: upload × type factor`,
    },
  ];
  return (
    <div className={styles.panel}>
      <h3 className={styles.title}>Storage in S3</h3>
      <div className={styles.levels}>
        <div className={styles.level}>
          <span className={styles.levelLabel}>Average over the range</span>
          <span className={styles.levelValue}>{num(total)} GB</span>
          <span className={styles.sub}>{usd(storageUsd)} for the range</span>
        </div>
        <div className={styles.level}>
          <span className={styles.levelLabel}>{endLabel}</span>
          <span className={styles.levelValue}>{num(s.endGb)} GB</span>
          <span className={styles.sub}>{num(s.endRecordedGb)} GB from records · {num(s.endEstimatedGb)} GB estimated</span>
        </div>
      </div>
      <p className={styles.note}>
        The bars show GB held on average over the range ({usd(storageUsd)}).
        {endsNow
          ? ' “Held now” is the figure to compare with Check the bucket, less its unfinished uploads and paths with no owner.'
          : ' Check the bucket lists the bucket today, so compare it with a range that ends today.'}
        {' '}Every upload, conversion and note attachment records the size of the folders it wrote,
        under the project owner. Files from before the records existed are estimated until
        {' '}<Link to="/admin/users" className={styles.link}>Recalculate storage</Link> reads their real size.
      </p>
      <div className={styles.chart}>
        {rows.map((r) => (
          <div key={r.key} className={styles.row} data-tip={`${r.label}: ${num(r.gb, 2)} GB · ${r.sub}`} tabIndex={0}>
            <div className={styles.label}>
              <span className={styles.name}>{r.label} <span className={styles.value}>{num(r.gb)} GB</span></span>
              <span className={styles.sub}>{r.sub}</span>
            </div>
            <div className={styles.track}>
              <div
                className={`${styles.bar} ${styles.storage} ${r.estimated ? styles.striped : ''}`}
                style={{ width: `${Math.min(100, (r.gb / max) * 100)}%` }}
              />
            </div>
          </div>
        ))}
        {s.byKind.length > 0 && (
          <div className={styles.group}>
            <p className={styles.groupName}>By folder kind</p>
            {s.byKind.map((k) => (
              <div key={k.key} className={styles.row} data-tip={`${k.label}: ${num(k.gb, 2)} GB`} tabIndex={0}>
                <div className={styles.label}>
                  <span className={styles.name}>{k.label} <span className={styles.value}>{num(k.gb)} GB</span></span>
                </div>
                <div className={styles.track}>
                  <div className={`${styles.bar} ${styles.storage}`} style={{ width: `${Math.min(100, (k.gb / max) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
        <div className={styles.axis} aria-hidden="true">
          <span />
          <div className={styles.ticks}>
            {[0, 0.5, 1].map((f, i) => <span key={i} style={{ left: `${f * 100}%` }}>{num(max * f)} GB</span>)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostOverview;
